import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { Dialog, Box, FormControl, TextField } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import HCPLogo from "../../assets/images/HCPLogo.png";

import "./SelectRepDialog.scss";
import { setSelectedEmail } from "../../redux/slice/get-rep-emails";
import { setSelectedName } from "../../redux/slice/get-rep-emails";
import { setProductGroup } from "../../redux/slice/get-rep-emails";
import { generateToken } from "../../redux/slice/generate-token";
import secureLocalStorage from "react-secure-storage";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export default function SelectRepDialog(props) {
  //   const { open, handleClose, name, userSessionId, handleRenamVal } = props;
  const dispatch = useDispatch();
  const location = useLocation();

  const { open, handleClose } = props;
  const repDetails = useSelector((state) => state.repEmails.repDetails);
  const isLoading = useSelector((state) => state.repEmails.isLoading);

  const selectedEmail = useSelector((state) => state.repEmails.selectedEmail);
  const selectedName = useSelector((state) => state.repEmails.selectedName);
  const isGenerating = useSelector((state) => state.accessToken.isLoading);
  const genError = useSelector((state) => state.accessToken.error);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailFromUrl = params.get("user_email");
    // const storedEmail = secureLocalStorage.getItem("userEmail");
    const initialEmail = emailFromUrl;
    if (initialEmail) {
      dispatch(setSelectedEmail(initialEmail));
    }
  }, [location.search, dispatch]);

  const reps = React.useMemo(() => {
    const byEmail = new Map();

    (repDetails || []).forEach((r) => {
      const key = r?.Sales_Rep_Email;
      const pg = r?.productgroup;

      if (!byEmail.has(key)) {
        byEmail.set(key, {
          ...r,
          productgroup: pg == null ? [] : [pg],
        });
      } else {
        const existing = byEmail.get(key);

        const arr = Array.isArray(existing.productgroup)
          ? existing.productgroup
          : existing.productgroup != null
            ? [existing.productgroup]
            : [];

        if (pg != null && !arr.includes(pg)) arr.push(pg);

        existing.productgroup = arr;
        byEmail.set(key, existing);
      }
    });

    const arr = Array.from(byEmail.values());
    arr.sort((a, b) => {
      const an = (a?.Sales_Rep_Name ?? "").trim();
      const bn = (b?.Sales_Rep_Name ?? "").trim();
      return an.localeCompare(bn, undefined, {
        sensitivity: "base",
        numeric: true,
      });
    });

    return arr;
  }, [repDetails]);

  const autoOptions = React.useMemo(() => {
    const cmp = (a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

    return (reps ?? []).map((r) => ({
      name: (r?.Sales_Rep_Name ?? "").trim(),
      email: r?.Sales_Rep_Email ?? "",
      groups: Array.isArray(r?.productgroup)
        ? [...r.productgroup].sort(cmp)
        : [],
    }));
  }, [reps]);

  const filter = createFilterOptions({
    stringify: (option) => option?.name ?? "",
  });

  const emailToName = React.useMemo(() => {
    const m = new Map();
    for (const r of reps) m.set(r.Sales_Rep_Email, r.Sales_Rep_Name);
    return m;
  }, [reps]);

  const emailToGroups = React.useMemo(() => {
    const cmp = (a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
    const m = new Map();
    for (const r of reps ?? []) {
      const groups = Array.isArray(r.productgroup) ? r.productgroup : [];
      m.set(r.Sales_Rep_Email, [...groups].sort(cmp));
    }
    return m;
  }, [reps]);

  // useEffect(() => {
  //   if (selectedEmail) {
  //     const name = emailToName.get(selectedEmail) || "";
  //     if (name && name !== selectedName) {
  //       dispatch(setSelectedName(name));
  //     }
  //     const groups = emailToGroups.get(selectedEmail) || [];
  //     // const groupAllOptions = groups.length > 1 ? ["All", ...groups] : groups;
  //     dispatch(setProductGroup(groups));
  //   }
  // }, [selectedEmail, emailToName, emailToGroups, selectedName, dispatch]);

  const handleGo = async () => {
    if (!selectedEmail) return;
    try {
      const token = await dispatch(generateToken(selectedEmail)).unwrap();

      secureLocalStorage.setItem("userEmail", selectedEmail);
      secureLocalStorage.setItem("token", token);

      const params = new URLSearchParams(location.search);
      params.set("user_email", selectedEmail);
      params.set("token", token);
      // navigate(
      //   { pathname: location.pathname, search: params.toString() },
      //   { replace: true }
      // );

      const queryString = `?${params.toString()}`;
      window.location.replace(`${location.pathname}${queryString}`);

      handleClose();
    } catch (err) {
      console.error("Failed to generate token:", err);
    }
  };

  return (
    <>
      {!isLoading && (
        <BootstrapDialog
          className="select-rep-dialog-parent"
          // onClose={handleClose}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            handleClose?.(event, reason);
          }}
          aria-labelledby="customized-dialog-title"
          open={open}
          maxWidth="xs"
          fullWidth
          disableEscapeKeyDown
          slotProps={{
            backdrop: {
              sx: { backgroundColor: "#F9FDFF", opacity: 1 },
            },
          }}
          PaperProps={{
            elevation: 0,
            sx: { boxShadow: "0px 4px 24px 0px #0000000D" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              pt: 3,
              pl: 2,
              pb: "4px",
            }}
          >
            <Box
              component="img"
              src={HCPLogo}
              alt="HCP logo"
              sx={{ height: "auto", width: "88px" }}
              draggable={false}
            />
          </Box>
          <DialogTitle
            sx={{
              m: 0,
              pt: "8px",
              px: "24px",
              pb: "0px",
              fontWeight: "bold",
              fontSize: "24px",
            }}
            id="customized-dialog-title"
          >
            Welcome
          </DialogTitle>
          <Typography
            sx={{ pl: "24px", fontSize: "14px" }}
            className="select-rep-dialog-sub-title"
          >
            Please choose a sales rep from the dropdown to proceed.
          </Typography>

          <Box sx={{ mx: "24px", mt: "32px", mb: 2 }}>
            <FormControl fullWidth size="medium" disabled={!reps.length}>
              {/* <InputLabel id="select-rep-email-label">Select</InputLabel> */}
              {/* <Select
                labelId="select-rep-email-label"
                id="select-rep-email"
                label="Email"
                value={selectedEmail}
                // onChange={(e) => dispatch(setSelectedEmail(e.target.value))}
                onChange={(e) => {
                  const email = e.target.value;
                  dispatch(setSelectedEmail(email));
                  const name = emailToName.get(email) || "";
                  dispatch(setSelectedName(name));
                  const groups = emailToGroups.get(email) || [];
                  dispatch(setProductGroup(groups));
                }}
                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
              >
                {reps.map((rep) => (
                  <MenuItem
                    key={rep.Sales_Rep_Email}
                    value={rep.Sales_Rep_Email}
                  >
                    {rep.Sales_Rep_Name}
                  </MenuItem>
                ))}
              </Select> */}
              <Autocomplete
                fullWidth
                size="small"
                // disablePortal
                autoHighlight
                clearOnEscape
                options={autoOptions}
                loading={isLoading}
                disabled={!autoOptions.length}
                value={
                  autoOptions.find((o) => o.email === selectedEmail) ?? null
                }
                filterOptions={filter}
                getOptionLabel={(opt) =>
                  typeof opt === "string" ? opt : (opt?.name ?? "")
                }
                isOptionEqualToValue={(opt, val) => opt.email === val.email}
                onChange={(_, option) => {
                  // Only set the email; your existing effect will derive name + groups
                  dispatch(setSelectedEmail(option?.email ?? ""));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Name"
                    placeholder="Start typing a name…"
                  />
                )}
                ListboxProps={{ style: { maxHeight: 200 } }}
                noOptionsText="No reps found"
              />
            </FormControl>

            {genError && (
              <Typography sx={{ mt: 1 }} variant="body2" color="error">
                {String(genError)}
              </Typography>
            )}
          </Box>

          {/* <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton> */}
          <DialogContent
            className="select-rep-dialog-content"
            sx={{
              px: "24px!important",
              pb: "24px!important",
              pt: "0px!important",
            }}
          >
            <Box className="select-rep-dialog-buttons-holder">
              {/* <Button
            variant="outlined"
            size="large"
            className="select-rep-dialog-cancel-button"
            onClick={handleClose}
          >
            Cancel
          </Button> */}
              <Button
                variant="contained"
                size="large"
                className="select-rep-dialog-go-button"
                onClick={handleGo}
                disabled={!selectedEmail || isGenerating}
              >
                Proceed
              </Button>
            </Box>
            <Typography
              variant="caption"
              component="div"
              sx={{ mt: "38px", textAlign: "center", color: "text.secondary" }}
            >
              © 2025 United Therapeutics Corporation. All Rights Reserved.
            </Typography>
          </DialogContent>
        </BootstrapDialog>
      )}
    </>
  );
}
